import { supabase } from '@/integrations/supabase/client';
import { RoadmapTemplate } from '@/types/roadmap';
import { handleError, CompatiblePromptField, generateUUID } from './utils';

export async function addTemplate(template: Omit<RoadmapTemplate, 'id'>): Promise<string> {
  try {
    // Insert roadmap template
    const { data: roadmapData, error: roadmapError } = await supabase
      .from('roadmap_templates')
      .insert({
        title: template.title,
        description: template.description || '',
        page_title: template.pageTitle || template.title,
        embed_url: template.embedUrl || '',
        embed_title: template.embedTitle || ''
      })
      .select()
      .single();
      
    if (roadmapError) throw roadmapError;
    
    const roadmapId = roadmapData.id;
    
    // Insert sidebar if provided
    let sidebarId;
    if (template.sidebar) {
      const { data: sidebarData } = await supabase
        .from('sidebars')
        .insert({ roadmap_id: roadmapId })
        .select()
        .single();
        
      sidebarId = sidebarData.id;
      
      // Insert sidebar tools
      if (template.sidebar.usefulTools && template.sidebar.usefulTools.length > 0) {
        await supabase
          .from('sidebar_tools')
          .insert(
            template.sidebar.usefulTools.map((tool: { label?: string; url?: string }, index) => ({
              sidebar_id: sidebarId,
              label: typeof tool === 'object' && tool.label ? tool.label : 
                     typeof tool === 'object' && tool.url ? tool.url : String(tool),
              url: typeof tool === 'object' && tool.url ? tool.url : '',
              position: index
            }))
          );
      }
      
      // Insert bonus activities
      if (template.sidebar.bonusActivities && template.sidebar.bonusActivities.length > 0) {
        await supabase
          .from('sidebar_activities')
          .insert(
            template.sidebar.bonusActivities.map((activity, index) => ({
              sidebar_id: sidebarId,
              title: activity.title,
              description: activity.description || '',
              icon_placeholder: activity.iconPlaceholder || '',
              href: activity.href || '#',
              position: index
            }))
          );
      }
      
      // Insert examples
      if (template.sidebar.examples && template.sidebar.examples.length > 0) {
        await supabase
          .from('sidebar_examples')
          .insert(
            template.sidebar.examples.map((example, index) => ({
              sidebar_id: sidebarId,
              text: example.text,
              href: example.href,
              description: example.description || '',
              position: index  // Position field is required
            }))
          );
      }
    }
    
    // Insert steps
    if (template.steps && template.steps.length > 0) {
      for (let i = 0; i < template.steps.length; i++) {
        const step = template.steps[i];
        
        // Insert step with walkthrough fields directly
        const { data: stepData } = await supabase
          .from('steps')
          .insert({
            roadmap_id: roadmapId,
            title: step.title,
            description: step.description || '',
            resources: step.resources || '',
            is_open_default: step.isOpenDefault || false,
            position: i,
            iframe_src: step.walkthrough?.iframeSrc || null,
            download_link: step.walkthrough?.downloadLink || null,
            download_text: step.walkthrough?.downloadText || null,
          })
          .select()
          .single();
          
        const stepId = stepData.id;
        
        // Insert links
        if (step.links && step.links.length > 0) {
          await supabase
            .from('links')
            .insert(
              step.links.map(link => ({
                step_id: stepId,
                label: link.label,
                url: link.url
              }))
            );
        }
        
        // Insert prompt module
        if (step.promptModule) {
          const { data: moduleData, error: moduleError } = await supabase
            .from('prompt_modules')
            .insert({
              step_id: stepId,
              title: step.promptModule.title || 'Prompt Module',
              copy_button_text: step.promptModule.copyButtonText || 'Copy Entire Prompt',
              introduction: step.promptModule.introduction || '',
              final_output_format: step.promptModule.finalOutputFormat || ''
            })
            .select('id')
            .single();

          if (moduleError) {
            console.error("Error inserting prompt module:", moduleError);
          } else if (moduleData) {
            const moduleId = moduleData.id;
            console.log("Inserted prompt module with ID:", moduleId, "for step:", stepId);

            const { error: stepUpdateError } = await supabase
              .from('steps')
              .update({ prompt_module_id: moduleId })
              .eq('id', stepId);

            if (stepUpdateError) {
              console.error('Error updating step with prompt_module_id:', stepUpdateError);
            }

            // Insert fields
            if (step.promptModule.fields && step.promptModule.fields.length > 0) {
              await supabase
                .from('prompt_fields')
                .insert(
                  step.promptModule.fields.map(field => {
                    const compatibleField = field as CompatiblePromptField;
                    const tagValue = compatibleField.type || compatibleField.tag || 'text';
                    
                    return {
                      prompt_module_id: moduleId,
                      label: compatibleField.label,
                      placeholder: compatibleField.placeholder || '',
                      example: compatibleField.example || '', // Added: HTML content for examples
                      tag: tagValue
                    };
                  })
                );
            }
            
            // Insert instructions
            if (step.promptModule.instructions && step.promptModule.instructions.length > 0) {
              await supabase
                .from('prompt_instructions')
                .insert(
                  step.promptModule.instructions.map((instruction, index) => ({
                    prompt_module_id: moduleId,
                    instruction,
                    position: index
                  }))
                );
            }
          }
        }
      }
    }
    
    return roadmapId;
    
  } catch (error) {
    return handleError(error, 'Error adding template');
  }
}
